import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  X,
  Upload,
  Link as LinkIcon,
  Save,
} from "lucide-react";
import { logger } from "../services/logService";
import sharedStepService from "../services/sharedStepService";
import ErrorBoundary from "../components/ErrorBoundary";
import toast from "react-hot-toast";

const SharedStepCreatePage = () => {
  const { sharedStepId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const projectId = searchParams.get("project_id");
  const location = window.location.pathname;
  const isViewMode = location.includes("/view/");
  const isEditMode = location.includes("/edit/") && !!sharedStepId;

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    steps: [{ step_number: 1, step_description: "", expected_result: "" }],
  });

  const [attachments, setAttachments] = useState([]);
  const [linkedTestCases, setLinkedTestCases] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  // Load existing shared step data for edit/view mode
  useEffect(() => {
    const loadSharedStep = async () => {
      if (!sharedStepId) {
        // Reset form for create mode
        setFormData({
          title: "",
          description: "",
          steps: [
            { step_number: 1, step_description: "", expected_result: "" },
          ],
        });
        return;
      }

      try {
        setLoadingData(true);
        const response =
          await sharedStepService.getSharedStepById(sharedStepId);
        const step =
          response?.data?.shared_step ||
          response?.data ||
          response?.shared_step ||
          response;

        if (step) {
          // Use step_description as title, and notes as description
          setFormData({
            title: step.step_description || "",
            description: step.notes || "",
            steps: [
              {
                step_number: 1,
                step_description: step.step_description || "",
                expected_result: step.expected_result || "",
              },
            ],
          });
        }
      } catch (error) {
        logger.error(error, "Failed to load shared step");
        toast.error("Failed to load shared step");
      } finally {
        setLoadingData(false);
      }
    };

    loadSharedStep();
  }, [sharedStepId]);

  const handleInputChange = (e) => {
    if (isViewMode) return; // Prevent changes in view mode

    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleStepChange = (index, field, value) => {
    if (isViewMode) return; // Prevent changes in view mode

    const newSteps = [...formData.steps];
    newSteps[index] = {
      ...newSteps[index],
      [field]: value,
    };
    setFormData((prev) => ({
      ...prev,
      steps: newSteps,
    }));

    // Clear step-specific error when user starts typing
    const errorKey = `step_${index}`;
    if (errors[errorKey]) {
      setErrors((prev) => ({
        ...prev,
        [errorKey]: null,
      }));
    }
  };

  const handleAddStep = () => {
    setFormData((prev) => ({
      ...prev,
      steps: [
        ...prev.steps,
        {
          step_number: prev.steps.length + 1,
          step_description: "",
          expected_result: "",
        },
      ],
    }));
  };

  const handleRemoveStep = (index) => {
    if (formData.steps.length === 1) {
      toast.error("At least one step is required");
      return;
    }

    const newSteps = formData.steps.filter((_, i) => i !== index);
    // Renumber steps
    const renumberedSteps = newSteps.map((step, i) => ({
      ...step,
      step_number: i + 1,
    }));

    setFormData((prev) => ({
      ...prev,
      steps: renumberedSteps,
    }));
  };

  const handleAddSharedStep = () => {
    // This would open a modal to select a shared step to add
    toast("Feature coming soon: Add Shared Step");
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    setAttachments((prev) => [...prev, ...files]);
    toast.success(`${files.length} file(s) added`);
  };

  const handleRemoveAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleLinkTestCases = () => {
    // This would open a modal to select test cases
    toast("Feature coming soon: Link Test Cases");
  };

  const handleCancel = () => {
    if (
      window.confirm(
        "Are you sure you want to cancel? All unsaved changes will be lost.",
      )
    ) {
      navigate(
        projectId ? `/shared-steps?project_id=${projectId}` : "/shared-steps",
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const newErrors = {};

      if (!formData.title.trim()) {
        newErrors.title = "Title is required";
      }

      if (!formData.description.trim()) {
        newErrors.description = "Description is required";
      }

      if (formData.steps.length === 0) {
        newErrors.steps = "At least one step is required";
      }
      if(!formData.steps?.find((item)=>item.step_description))
      formData.steps.forEach((step, index) => {
        if (!step.step_description.trim()) {
          newErrors[`step_${index}`] =
            `Step ${index + 1} description is required`;
        }
      });

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        setLoading(false);
        return;
      }

      // Prepare data for API
      const stepData = {
        project_id: projectId,
        step_description: formData.steps[0].step_description, // For single step, use first step
        expected_result: formData.steps[0].expected_result,
        notes: formData.description,
        // If your API supports multiple steps, you might need to adjust this
      };

      if (isEditMode) {
        await sharedStepService.updateSharedStep(sharedStepId, stepData);
        toast.success("Shared step updated successfully!");
      } else {
        await sharedStepService.createSharedStep(stepData);
        toast.success("Shared step created successfully!");
      }
      navigate(`/shared-steps?project_id=${projectId}`);
    } catch (error) {
      logger.error(error, "Failed to create shared step");
      toast.error(error.message || "Failed to create shared step");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-white">
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() =>
                  navigate(`/shared-steps?project_id=${projectId}`)
                }
                className="text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <span className="text-sm text-gray-600">
                Shared Steps /{" "}
                {isViewMode ? "View" : isEditMode ? "Edit" : "Create"}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleCancel}
                className={`px-4 py-2 border rounded-md font-medium ${isViewMode
                  ? "text-gray-600 border-gray-300 hover:bg-gray-50"
                  : "text-red-600 border-red-600 hover:bg-red-50"
                  }`}
              >
                {isViewMode ? "Back" : "Cancel"}
              </button>
              {isViewMode ? (
                <button
                  onClick={() =>
                    navigate(
                      `/shared-steps/edit/${sharedStepId}?project_id=${projectId}`,
                    )
                  }
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-medium flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Edit
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={loading || loadingData}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isEditMode ? (
                    <Save className="w-4 h-4" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                  {loading
                    ? isEditMode
                      ? "Updating..."
                      : "Creating..."
                    : isEditMode
                      ? "Update Shared Steps"
                      : "Create Shared Steps"}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="px-6 py-6">
          <div className="flex gap-6">
            {/* Main Content */}
            <div className="flex-1">
              {/* Shared Step Details */}
              <div className="mb-6">
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter the Title."
                  readOnly={isViewMode}
                  disabled={loadingData || isViewMode}
                  className={`w-full text-2xl font-semibold text-gray-900 border-none outline-none focus:ring-0 placeholder:text-gray-400 ${isViewMode ? "bg-transparent cursor-default" : ""
                    } ${loadingData ? "opacity-50" : ""}`}
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-500">{errors.title}</p>
                )}
              </div>

              <div className="mb-6">
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Describe your share step"
                  rows="4"
                  readOnly={isViewMode}
                  disabled={loadingData || isViewMode}
                  className={`w-full px-3 py-2 text-gray-700 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-y ${isViewMode ? "bg-gray-50 cursor-default" : ""
                    } ${loadingData ? "opacity-50" : ""}`}
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.description}
                  </p>
                )}
              </div>

              {/* Steps Section */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Steps
                </h3>

                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase w-16">
                          #
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                          Steps
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                          Expected results
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase w-16"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {formData.steps.map((step, index) => (
                        <>
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {step.step_number}
                            </td>
                            <td className="px-4 py-3">
                              <input
                                type="text"
                                value={step.step_description}
                                onChange={(e) =>
                                  handleStepChange(
                                    index,
                                    "step_description",
                                    e.target.value,
                                  )
                                }
                                placeholder="Enter step description"
                                readOnly={isViewMode}
                                disabled={loadingData || isViewMode}
                                className={`w-full px-2 py-1 text-sm text-gray-700 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${isViewMode
                                  ? "bg-gray-50 cursor-default"
                                  : "bg-white"
                                  } ${loadingData ? "opacity-50" : ""}`}
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                type="text"
                                value={step.expected_result}
                                onChange={(e) =>
                                  handleStepChange(
                                    index,
                                    "expected_result",
                                    e.target.value,
                                  )
                                }
                                placeholder="Enter expected result"
                                readOnly={isViewMode}
                                disabled={loadingData || isViewMode}
                                className={`w-full px-2 py-1 text-sm text-gray-700 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${isViewMode
                                  ? "bg-gray-50 cursor-default"
                                  : "bg-white"
                                  } ${loadingData ? "opacity-50" : ""}`}
                              />
                            </td>
                            <td className="px-4 py-3">
                              {!isViewMode && (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveStep(index)}
                                  disabled={loadingData}
                                  className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded disabled:opacity-50"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              )}
                            </td>
                          </tr>
                          {errors[`step_${index}`] && (
                            <tr className="bg-gray-50">
                              <td colSpan={4} className="px-4 py-2">
                                <p className="text-sm text-red-500">
                                  {errors[`step_${index}`]}
                                </p>
                              </td>
                            </tr>
                          )}
                        </>

                      ))}
                    </tbody>
                  </table>
                </div>

                {console.log({ errors })}


                {!isViewMode && (
                  <div className="flex items-center gap-3 mt-4">
                    <button
                      type="button"
                      onClick={handleAddStep}
                      disabled={loadingData}
                      className="px-4 py-2 text-indigo-600 border border-indigo-600 rounded-md hover:bg-indigo-50 font-medium flex items-center gap-2 disabled:opacity-50"
                    >
                      <Plus className="w-4 h-4" />
                      Add Step
                    </button>
                    {/* <button
                      type="button"
                      onClick={handleAddSharedStep}
                      disabled={loadingData}
                      className="px-4 py-2 text-indigo-600 border border-indigo-600 rounded-md hover:bg-indigo-50 font-medium flex items-center gap-2 disabled:opacity-50"
                    >
                      <Plus className="w-4 h-4" />
                      Add Shared Step
                    </button> */}
                  </div>
                )}
              </div>
            </div>

            {/* Right Sidebar */}
            <div className="w-80 space-y-6">
              {/* Attachments Section */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">
                  Attachments
                </h4>
                <input
                  type="file"
                  id="file-upload"
                  multiple
                  onChange={handleFileUpload}
                  disabled={isViewMode}
                  className="hidden"
                />
                <label
                  htmlFor="file-upload"
                  className={`block w-full px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md text-center flex items-center justify-center gap-2 ${isViewMode
                    ? "bg-gray-100 cursor-not-allowed opacity-50"
                    : "hover:bg-gray-100 cursor-pointer"
                    }`}
                >
                  <Upload className="w-4 h-4" />
                  Upload Files
                </label>
                {attachments.length === 0 ? (
                  <p className="mt-3 text-xs text-gray-500 text-center">
                    No files attached yet.
                  </p>
                ) : (
                  <div className="mt-3 space-y-2">
                    {attachments.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-white rounded border border-gray-200"
                      >
                        <span className="text-xs text-gray-700 truncate flex-1">
                          {file.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveAttachment(index)}
                          className="ml-2 text-red-600 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Linked Test Cases Section */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">
                  Linked Test Cases
                </h4>
                {linkedTestCases.length === 0 ? (
                  <>
                    <p className="text-xs text-gray-500 mb-3">
                      No test cases have been linked.
                    </p>
                    <button
                      type="button"
                      onClick={handleLinkTestCases}
                      disabled={isViewMode}
                      className={`w-full px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md flex items-center justify-center gap-2 ${isViewMode
                        ? "bg-gray-100 cursor-not-allowed opacity-50"
                        : "hover:bg-gray-100 cursor-pointer"
                        }`}
                    >
                      <LinkIcon className="w-4 h-4" />
                      Link Test Cases
                    </button>
                  </>
                ) : (
                  <div className="space-y-2">
                    {linkedTestCases.map((testCase, index) => (
                      <div
                        key={index}
                        className="p-2 bg-white rounded border border-gray-200"
                      >
                        <p className="text-xs text-gray-700">
                          {testCase.title}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default SharedStepCreatePage;
